import React, { useState, useCallback } from "react";
import {
  Modal,
  Upload,
  Table,
  Button,
  Alert,
  Descriptions,
  Tag,
  message,
  Spin,
  Radio,
  Input,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import GETJwtToken from "../../../redux/authentication/authentication";
import { extractEnvelopeWithHash } from "../../../flatbuffers/envelope";
import { verifyRootAsOrgExport } from "../../../flatbuffers/generated/archive/org-export";
import type { ImportSummary } from "../../../interfaces/Import.interface";
import ImportWarnings from "./ImportWarnings";
import {
  IMPORT_FILE_ACCEPT,
  IMPORT_FILE_HINT,
  IMPORT_FILE_TYPES_LABEL,
  importFileExtension,
  isImportFile,
} from "../utils/importFileTypes";

const { Dragger } = Upload;

interface WizardImportDialogProps {
  orgId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FieldDiff {
  field: string;
  existing: string;
  imported: string;
}

interface AdapterConflict {
  imported_name: string;
  imported_version: string;
  existing_id: string;
  field_diffs?: FieldDiff[];
}

interface AnalysisData {
  data: {
    org_count: number;
    server_count: number;
    adapter_count: number;
    channel_count: number;
    bank_count: number;
    lift_count: number;
    key_count: number;
    user_count: number;
    role_count: number;
    notification_count: number;
    id: string;
    adapter_conflicts?: AdapterConflict[];
    adapter_names?: string[];
    [key: string]: unknown;
  };
}

type ConflictAction = "replace" | "skip" | "rename";

interface ConflictResolution {
  action: ConflictAction;
  newName: string;
}

/** Returns a unique key for a conflict used as a map key and React key. */
function conflictKey(c: AdapterConflict): string {
  return `${c.imported_name}|${c.imported_version}`;
}

function WizardImportDialog({
  orgId,
  visible,
  onClose,
  onSuccess = undefined,
}: WizardImportDialogProps) {
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [conflictResolutions, setConflictResolutions] = useState<
    Record<string, ConflictResolution>
  >({});
  const [analyzing, setAnalyzing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE;

  const handleFileUpload = useCallback(
    async (file: UploadFile) => {
      if (!file.originFileObj) return;
      setAnalyzing(true);
      setError(null);
      setAnalysis(null);
      setConflictResolutions({});

      try {
        // For .lnx (FlatBuffer binary) files, verify envelope and structure before uploading.
        if (file.name.endsWith(".lnx") || file.name.endsWith(".bin")) {
          const buffer = await file.originFileObj.arrayBuffer();
          const bytes = new Uint8Array(buffer);

          const envelopeContents = extractEnvelopeWithHash(bytes);
          if (!envelopeContents) {
            setError("Invalid binary file: missing or corrupt LN2E envelope header.");
            setAnalyzing(false);
            return;
          }

          // Strip size prefix (4 bytes) before FlatBuffer verification.
          const payload = envelopeContents.payload;
          const fbPayload = payload.length > 4 ? payload.subarray(4) : payload;

          try {
            const view = new DataView(fbPayload.buffer, fbPayload.byteOffset, fbPayload.byteLength);
            verifyRootAsOrgExport(view);
          } catch (verifyErr) {
            const detail = verifyErr instanceof Error ? verifyErr.message : "unknown error";
            setError(`Invalid binary file: FlatBuffer verification failed \u2014 ${detail}`);
            setAnalyzing(false);
            return;
          }
        }

        const token = await GETJwtToken();
        const formData = new FormData();
        formData.append("file", file.originFileObj);
        formData.append("parent_org_id", orgId);

        const response = await fetch(`${baseUrl}/import/organization/analyze`, {
          method: "POST",
          headers: { authorization: token },
          body: formData,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const detail = body?.message ?? `status ${response.status}`;
          throw new Error(`Analysis failed: ${detail}`);
        }

        const result = await response.json();
        const d = result?.data;

        // eslint-disable-next-line no-console
        console.log(
          "[WizardImportDialog] Analyze response:",
          JSON.stringify(
            {
              adapter_count: d?.adapter_count,
              adapter_names: d?.adapter_names,
              adapter_conflicts: d?.adapter_conflicts,
              server_count: d?.server_count,
              org_count: d?.org_count,
              id: d?.id,
            },
            null,
            2,
          ),
        );

        if (
          d &&
          !d.org_count &&
          !d.server_count &&
          !d.adapter_count &&
          !d.channel_count &&
          !d.bank_count &&
          !d.lift_count &&
          !d.key_count &&
          !d.user_count &&
          !d.role_count &&
          !d.notification_count
        ) {
          setError(
            "The file was parsed successfully but contains no importable data. " +
              "This may indicate the export was scoped to an entity that could not be resolved " +
              "(e.g. an adapter not assigned to a server under the selected organization). " +
              "Try re-exporting from the correct organization or using a full-org export.",
          );
          setAnalyzing(false);
          return;
        }

        // Initialize conflict resolutions to "replace" (existing behavior) so
        // the Execute button is immediately enabled. Users can change per conflict.
        const conflicts: AdapterConflict[] = d?.adapter_conflicts ?? [];
        setConflictResolutions(
          conflicts.reduce<Record<string, ConflictResolution>>((acc, c) => {
            acc[conflictKey(c)] = { action: "replace", newName: "" };
            return acc;
          }, {}),
        );

        setAnalysis(result);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Analysis failed";
        setError(msg);
      } finally {
        setAnalyzing(false);
      }
    },
    [orgId, baseUrl],
  );

  /** True when the Execute button should be disabled due to incomplete resolution. */
  const hasIncompleteRename = useCallback(
    (): boolean =>
      Object.values(conflictResolutions).some((r) => r.action === "rename" && !r.newName.trim()),
    [conflictResolutions],
  );

  const handleExecute = useCallback(async () => {
    if (!analysis) return;
    setExecuting(true);
    setError(null);

    try {
      const token = await GETJwtToken();
      const response = await fetch(`${baseUrl}/import/organization/execute`, {
        method: "POST",
        headers: {
          authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          analysis_id: analysis.data.id,
          resolutions: (analysis.data.adapter_conflicts ?? []).map((c) => {
            const key = conflictKey(c);
            const res = conflictResolutions[key] ?? { action: "replace", newName: "" };
            return {
              adapter_name: c.imported_name,
              adapter_version: c.imported_version,
              action: res.action,
              ...(res.action === "rename" ? { new_name: res.newName.trim() } : {}),
            };
          }),
          adopt_existing_users: true,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        const detail =
          (errBody as Record<string, unknown> | null)?.message ?? `status ${response.status}`;
        throw new Error(`Import failed: ${detail}`);
      }

      const result = await response.json().catch(() => null);
      const summaryData: ImportSummary | null = result?.data ?? null;
      setSummary(summaryData);

      // eslint-disable-next-line no-console
      console.log("[WizardImportDialog] Execute response (full):", JSON.stringify(result, null, 2));
      // eslint-disable-next-line no-console
      console.log("[WizardImportDialog] Summary data:", JSON.stringify(summaryData, null, 2));

      // Check if expected entities were actually created. The backend
      // silently skips INSERT statements that fail with recoverable
      // PostgreSQL errors (e.g. FK violations when a binary export
      // references a server that doesn't exist in this environment).
      const expectedAdapters = analysis.data.adapter_count ?? 0;
      const actualAdapters =
        (summaryData?.adapters_created ?? 0) + (summaryData?.adapters_replaced ?? 0);
      const hasSkippedEntities = expectedAdapters > 0 && actualAdapters < expectedAdapters;

      // eslint-disable-next-line no-console
      console.log("[WizardImportDialog] Adapter check:", {
        expectedAdapters,
        actualAdapters,
        adapters_created: summaryData?.adapters_created,
        adapters_replaced: summaryData?.adapters_replaced,
        adapters_skipped: summaryData?.adapters_skipped,
        hasSkippedEntities,
      });

      setSuccess(true);

      if (hasSkippedEntities) {
        message.warning(
          `Import completed but ${expectedAdapters - actualAdapters} adapter(s) were skipped`,
        );
      } else {
        message.success("Import completed successfully");
      }

      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      setError(msg);
    } finally {
      setExecuting(false);
    }
  }, [analysis, conflictResolutions, baseUrl, onSuccess]);

  const handleClose = useCallback(() => {
    setAnalysis(null);
    setConflictResolutions({});
    setError(null);
    setSuccess(false);
    setSummary(null);
    onClose();
  }, [onClose]);

  const handleDraggerChange = useCallback(
    (info: { fileList: UploadFile[] }) => {
      if (info.fileList.length > 0) {
        handleFileUpload(info.fileList[0]);
      }
    },
    [handleFileUpload],
  );

  function buildAnalysisColumns() {
    return [
      { title: "Entity", dataIndex: "entity", key: "entity" },
      { title: "Count", dataIndex: "count", key: "count" },
    ];
  }

  function buildAnalysisData() {
    if (!analysis) return [];
    const d = analysis.data;
    return [
      { key: "orgs", entity: "Organizations", count: d.org_count },
      { key: "servers", entity: "Servers", count: d.server_count },
      { key: "adapters", entity: "Adapters", count: d.adapter_count },
      { key: "channels", entity: "Channels", count: d.channel_count },
      { key: "banks", entity: "Banks", count: d.bank_count },
      { key: "lifts", entity: "Lifts", count: d.lift_count },
      { key: "keys", entity: "Keys", count: d.key_count },
      { key: "users", entity: "Users", count: d.user_count },
      { key: "roles", entity: "Roles", count: d.role_count },
      { key: "notifications", entity: "Notifications", count: d.notification_count },
    ];
  }

  function renderConflicts() {
    const conflicts = analysis?.data.adapter_conflicts;
    if (!conflicts || conflicts.length === 0) return null;

    return (
      <div style={{ marginTop: 16 }}>
        <Alert
          type="warning"
          message={`${conflicts.length} Adapter Conflict${conflicts.length > 1 ? "s" : ""} Found`}
          description="The following adapters already exist. Choose how to handle each one:"
          showIcon
          style={{ marginBottom: 12 }}
        />
        {conflicts.map((conflict) => {
          const key = conflictKey(conflict);
          const res = conflictResolutions[key] ?? { action: "replace", newName: "" };

          return (
            <div
              key={key}
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <div style={{ marginBottom: 6 }}>
                <strong>{conflict.imported_name}</strong>
                <span style={{ color: "#888", marginLeft: 8 }}>v{conflict.imported_version}</span>
              </div>
              {conflict.field_diffs && conflict.field_diffs.length > 0 && (
                <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                  Differs:{" "}
                  {conflict.field_diffs
                    .map((d) => `${d.field} (existing: ${d.existing}, imported: ${d.imported})`)
                    .join("; ")}
                </div>
              )}
              <Radio.Group
                value={res.action}
                onChange={(e) =>
                  setConflictResolutions((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], action: e.target.value as ConflictAction },
                  }))
                }
              >
                <Radio value="replace">Replace existing</Radio>
                <Radio value="skip">Keep existing (skip import)</Radio>
                <Radio value="rename">Rename imported</Radio>
              </Radio.Group>
              {res.action === "rename" && (
                <Input
                  placeholder="New adapter name"
                  value={res.newName}
                  status={res.newName.trim() ? undefined : "error"}
                  onChange={(e) =>
                    setConflictResolutions((prev) => ({
                      ...prev,
                      [key]: { ...prev[key], newName: e.target.value },
                    }))
                  }
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function buildAdapterOutcomes(): Array<{
    name: string;
    storedAs: string;
    status: "created" | "replaced" | "skipped" | "failed";
    reason: string;
  }> {
    const allNames = analysis?.data.adapter_names ?? [];
    const conflicts = analysis?.data.adapter_conflicts ?? [];
    const conflictNameSet = new Set(conflicts.map((c) => c.imported_name));

    const outcomes: Array<{
      name: string;
      storedAs: string;
      status: "created" | "replaced" | "skipped" | "failed";
      reason: string;
    }> = [];

    // Adapters with detected conflicts — resolution determines outcome.
    conflicts.forEach((c) => {
      const key = conflictKey(c);
      const res = conflictResolutions[key] ?? { action: "replace", newName: "" };

      if (res.action === "replace") {
        outcomes.push({
          name: c.imported_name,
          storedAs: c.imported_name,
          status: "replaced",
          reason: "Replaced existing adapter (same name + version)",
        });
      } else if (res.action === "skip") {
        outcomes.push({
          name: c.imported_name,
          storedAs: c.imported_name,
          status: "skipped",
          reason: "Kept existing adapter (user chose skip)",
        });
      } else if (res.action === "rename") {
        outcomes.push({
          name: c.imported_name,
          storedAs: res.newName.trim() || c.imported_name,
          status: "created",
          reason: `Renamed to avoid conflict with existing "${c.imported_name}"`,
        });
      }
    });

    // Adapters without conflicts.
    const expectedAdapters = analysis?.data.adapter_count ?? 0;
    const actualCreated = summary?.adapters_created ?? 0;
    const actualReplaced = summary?.adapters_replaced ?? 0;
    const totalSuccessful = actualCreated + actualReplaced;
    const conflictCreated = outcomes.filter(
      (o) => o.status === "created" || o.status === "replaced",
    ).length;
    const nonConflictSuccessful = totalSuccessful - conflictCreated;

    const nonConflictNames = allNames.filter((n) => !conflictNameSet.has(n));
    const nonConflictExpected = expectedAdapters - conflicts.length;
    const nonConflictFailed = nonConflictExpected - nonConflictSuccessful;

    // Extract adapter-specific errors from skipped_errors (format: "[adapters] error text").
    const adapterErrors = (summary?.skipped_errors ?? []).filter((e) => e.startsWith("[adapters]"));

    for (let i = 0; i < nonConflictNames.length; i += 1) {
      const adapterName = nonConflictNames[i];
      const isFailed = i >= nonConflictSuccessful && nonConflictFailed > 0;

      if (isFailed) {
        const pgError = adapterErrors.length > 0 ? adapterErrors.shift() : null;
        outcomes.push({
          name: adapterName,
          storedAs: adapterName,
          status: "failed",
          reason: pgError
            ? `Insert failed: ${pgError.replace("[adapters] ", "")}`
            : "No conflict detected (no existing adapter with same name + version). " +
              "Insert was silently skipped by the backend.",
        });
      } else {
        outcomes.push({
          name: adapterName,
          storedAs: adapterName,
          status: "created",
          reason: "No conflict — created as new adapter",
        });
      }
    }

    return outcomes;
  }

  function renderImportSummary() {
    if (!summary) {
      return (
        <Alert
          type="success"
          message="Import Successful"
          description="Configuration has been imported successfully."
          showIcon
          style={{ marginTop: 16 }}
        />
      );
    }

    const expectedAdapters = analysis?.data.adapter_count ?? 0;
    const actualAdapters = (summary.adapters_created ?? 0) + (summary.adapters_replaced ?? 0);
    const hasSkipped = expectedAdapters > 0 && actualAdapters < expectedAdapters;
    const adapterOutcomes = expectedAdapters > 0 ? buildAdapterOutcomes() : [];

    const statusTag = (status: "created" | "replaced" | "skipped" | "failed") => {
      const map = {
        created: { color: "success", label: "Created" },
        replaced: { color: "processing", label: "Replaced" },
        skipped: { color: "default", label: "Skipped" },
        failed: { color: "error", label: "Failed" },
      };
      const { color, label } = map[status];
      return <Tag color={color}>{label}</Tag>;
    };

    return (
      <>
        {hasSkipped ? (
          <Alert
            type="warning"
            message="Import Partially Completed"
            description={
              `${expectedAdapters - actualAdapters} adapter(s) were not imported. ` +
              "See details below for each adapter."
            }
            showIcon
            style={{ marginTop: 16 }}
          />
        ) : (
          <Alert
            type="success"
            message="Import Successful"
            description="Configuration has been imported successfully."
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {adapterOutcomes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong>Adapter Details</strong>
            {adapterOutcomes.map((outcome) => (
              <div
                key={outcome.name}
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  padding: 10,
                  marginTop: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {statusTag(outcome.status)}
                  <strong>{outcome.storedAs}</strong>
                  {outcome.storedAs !== outcome.name && (
                    <span style={{ color: "#888", fontSize: 12 }}>
                      (imported as &ldquo;{outcome.name}&rdquo;)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{outcome.reason}</div>
              </div>
            ))}
          </div>
        )}
        <Descriptions bordered size="small" column={2} style={{ marginTop: 16 }}>
          {summary.orgs_created > 0 && (
            <Descriptions.Item label="Organizations">{summary.orgs_created}</Descriptions.Item>
          )}
          {summary.servers_created > 0 && (
            <Descriptions.Item label="Servers">{summary.servers_created}</Descriptions.Item>
          )}
          {summary.channels_created > 0 && (
            <Descriptions.Item label="Channels">{summary.channels_created}</Descriptions.Item>
          )}
          {summary.banks_created > 0 && (
            <Descriptions.Item label="Banks">{summary.banks_created}</Descriptions.Item>
          )}
          {summary.lifts_created > 0 && (
            <Descriptions.Item label="Lifts">{summary.lifts_created}</Descriptions.Item>
          )}
          {summary.keys_created > 0 && (
            <Descriptions.Item label="Keys">{summary.keys_created}</Descriptions.Item>
          )}
          {summary.notifications_created > 0 && (
            <Descriptions.Item label="Notifications">
              {summary.notifications_created}
            </Descriptions.Item>
          )}
        </Descriptions>
        <ImportWarnings warnings={summary.warnings} />
      </>
    );
  }

  return (
    <Modal
      title="Import Configuration"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      {!analysis && !success && (
        <Dragger
          accept={IMPORT_FILE_ACCEPT}
          maxCount={1}
          beforeUpload={(file) => {
            if (!isImportFile(file.name)) {
              setError(
                `Unsupported file type "${importFileExtension(file.name)}". Use ${IMPORT_FILE_TYPES_LABEL}.`,
              );
              return Upload.LIST_IGNORE;
            }

            return false;
          }}
          onChange={handleDraggerChange}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to upload</p>
          <p className="ant-upload-hint">{IMPORT_FILE_HINT}</p>
        </Dragger>
      )}

      {analyzing && <Spin tip="Analyzing file..." />}

      {error && (
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {analysis && !success && (
        <div>
          <Table
            columns={buildAnalysisColumns()}
            dataSource={buildAnalysisData()}
            pagination={false}
            size="small"
            style={{ marginTop: 16 }}
          />
          {renderConflicts()}
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button onClick={handleClose} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleExecute}
              loading={executing}
              disabled={hasIncompleteRename()}
            >
              Execute Import
            </Button>
          </div>
        </div>
      )}

      {success && renderImportSummary()}
    </Modal>
  );
}

export default WizardImportDialog;
